import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Button, Paper, AppBar, Toolbar, Typography, Chip, Avatar} from '@material-ui/core';
import {SecondaryNavigation} from 'components/Navigation';
import {formatErrors} from 'actions/errors';
import {addFlashMessage} from 'actions/flash-message';
import {fetchScrollableTips, clearScrollableTips} from 'actions/tips';
import * as permissions from 'utils/permissions';

const buildTipData = (tip) => {
  return {
    id: tip.get('id'),
    record: tip.toJS(),
    title: tip.get('title'),
    body: tip.getIn(['body']),
    type: tip.get('type'),
  };
};

class TipScrollable extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {
    tipPages: PropTypes.object.isRequired,
    fetchScrollableTips: PropTypes.func.isRequired,
    clearScrollableTips: PropTypes.func.isRequired,
    formatErrors: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isFetching: false,
      type: undefined,
    };
    this.setFetchCompleted = this.setFetchCompleted.bind(this);
    this.fetchTips = this.fetchTips.bind(this);
    this.scrollIntoLoadMore = this.scrollIntoLoadMore.bind(this);
    this.setFilter = this.setFilter.bind(this);
    this.getTipFilters = this.getTipFilters.bind(this);
  }

  componentDidMount() {
    this.fetchTips();
    window.addEventListener('scroll', this.scrollIntoLoadMore);
  }

  componentWillUnmount() {
    this.props.clearScrollableTips();
    window.removeEventListener('scroll', this.scrollIntoLoadMore);
  }

  setFetchCompleted() {
    this.setState({
      isFetching: false,
    });
  }

  fetchTips() {
    const {type} = this.state;
    const {fetchScrollableTips, formatErrors} = this.props;
    this.setState({
      isFetching: true,
    });
    return fetchScrollableTips({type})
      .then(this.setFetchCompleted)
      .catch((err) => formatErrors(err).then(() => this.setFetchCompleted()));
  }

  scrollIntoLoadMore() {
    if (!this.loadMoreButton) {
      return;
    }
    if (this.loadMoreButton.offsetTop <= window.innerHeight) {
      this.fetchTips();
    }
  }

  setFilter(type) {
    this.setState(
      (prevState) => ({
        type: prevState.type === type ? undefined : type,
      }),
      () => {
        this.props.clearScrollableTips();
        this.fetchTips();
      },
    );
  }

  getTipFilters() {
    const {type} = this.state;
    return {
      schoolCoordinator: (
        <Chip
          avatar={<Avatar>S</Avatar>}
          label="School Coordinator Tips"
          className={classNames({'tip-school_coordinator': type === 'school_coordinator'})}
          onClick={() => this.setFilter('school_coordinator')}
        />
      ),
      teacher: (
        <Chip
          avatar={<Avatar>T</Avatar>}
          label="Teacher Tips"
          className={classNames({'tip-teacher': type === 'teacher'})}
          onClick={() => this.setFilter('teacher')}
        />
      ),
      parent: (
        <Chip
          avatar={<Avatar>P</Avatar>}
          label="Parent Tips"
          className={classNames({'tip-parent': type === 'parent'})}
          onClick={() => this.setFilter('parent')}
        />
      ),
      reader: (
        <Chip
          avatar={<Avatar>R</Avatar>}
          label="Student Tips"
          className={classNames({'tip-reader': type === 'reader'})}
          onClick={() => this.setFilter('reader')}
        />
      ),
      // book: (
      //   <Chip
      //     avatar={<Avatar>B</Avatar>}
      //     label="Book Tips"
      //     className={classNames({'tip-book': type === 'book'})}
      //     onClick={() => this.setFilter('book')}
      //   />
      // )
    };
  }

  render() {
    const {role, tipPages} = this.props;
    const pageIds = tipPages.get('pageIds', []);
    const tipList = tipPages.get('tips', {});
    const hasNext = tipPages.get('hasNext');
    const tipFilters = this.getTipFilters();
    return (
      <React.Fragment>
        {permissions.isReader(role) && (
          <SecondaryNavigation
            tabs={[
              {url: '/readings', label: 'Journal', isActive: false},
              {url: '/tips', label: 'Tips', isActive: true},
              {url: 'http://blog.kluwell.com/', label: 'Resources', isActive: false},
              {url: '/awards', label: 'Awards', isActive: false},
            ]}
          />
        )}
        <div className="tip-scrollable container content-box">
          <AppBar position="static" color="primary" className="section-app-bar">
            <Toolbar>
              <Typography variant="h6">Reading Tips</Typography>
              <div>
                <span>Filters: </span>
                {permissions.isSchoolCoordinator(role) ? (
                  <React.Fragment>
                    {tipFilters.schoolCoordinator}
                    {tipFilters.teacher}
                    {/*{tipFilters.parent}*/}
                    {tipFilters.reader}
                  </React.Fragment>
                ) : permissions.isTeacher(role) ? (
                  <React.Fragment>
                    {tipFilters.teacher}
                    {/*{tipFilters.parent}*/}
                    {tipFilters.reader}
                  </React.Fragment>
                ) : permissions.isParent(role) ? (
                  <React.Fragment>
                    {tipFilters.parent}
                    {tipFilters.reader}
                  </React.Fragment>
                ) : (
                  permissions.isReader(role) && (
                    <React.Fragment>
                      {tipFilters.parent}
                      {tipFilters.reader}
                    </React.Fragment>
                  )
                )}
              </div>
            </Toolbar>
          </AppBar>
          {pageIds.map((pageId) =>
            tipList.get(pageId, []).map((tip) => {
              tip = buildTipData(tip);
              return (
                <Paper key={tip.id} className="tip-row">
                  <div>
                    <div
                      className={classNames({
                        'tip-type': true,
                        [`tip-${tip.type}`]: true,
                      })}
                    >
                      {tip.type[0]}
                    </div>
                    <Typography variant="h6" className="tip-title">
                      {tip.title}
                    </Typography>
                  </div>
                  <div className="tip-body">{tip.body}</div>
                </Paper>
              );
            }),
          )}
          {hasNext && (
            <Button ref={(ref) => (this.loadMoreButton = ref)} color="secondary" fullWidth onClick={this.fetchTips}>
              Load More
            </Button>
          )}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  role: state.account.getIn(['user', 'role']),
  tipPages: state.tips.get('scrollableTipPages'),
});

const mapDispatchToProps = (dispatch) => ({
  formatErrors: (...args) => dispatch(formatErrors(...args)),
  addFlashMessage: (...args) => dispatch(addFlashMessage(...args)),
  fetchScrollableTips: (...args) => dispatch(fetchScrollableTips(...args)),
  clearScrollableTips: (...args) => dispatch(clearScrollableTips(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TipScrollable);
